import { Entity, isSystemEntity } from '@backstage/catalog-model';
import { Application } from '../components/insights/types/InsightsTypes';
import { RepoUrlPickerState } from '../components';

type PolicyName =
  | 'UpdateApplicationPolicy'
  | 'ShowbackTabPolicy'
  | 'UpdateTagPolicy';

/**
 * Indicates whether a feature is enabled.
 * @param app The application to check
 * @param policyName The policy name to check
 */
export function canUserUpdateApp(
  app: Application,
  policyName: PolicyName,
): (entity: Entity) => boolean {
  return (entity: Entity): boolean => {
    // Doesn't apply to non-system entities
    if (!isSystemEntity(entity)) {
      return false;
    }

    if (
      app?.authorizationPolicy &&
      app?.authorizationPolicy?.length > 0 &&
      app?.authorizationPolicy?.some(
        x => x.policy === policyName && x.hasAccess,
      )
    ) {
      return true;
    }
    return false;
  };
}

/**
 * Performs some check on an entity.
 */
export type EntityCheckFunction = (entity: Entity) => boolean;

/**
 * Returns a function that checks if an entity matches all the given entity check functions.
 * @param entityCheckFunctions The entity check functions
 */
export function andConditions(
  ...entityCheckFunctions: EntityCheckFunction[]
): EntityCheckFunction {
  return (entity: Entity): boolean => {
    return entityCheckFunctions.every(entityCheckFunction =>
      entityCheckFunction(entity),
    );
  };
}

/**
 * Parses a source URL into its constituent components
 *
 * @param input A string containing a repo URL in `url:<protocol>://<host>/<owner>/<repo>/tree/<branch>` format.
 */
export function parseSourceUrl(input: string): {
  protocol: string;
  host: string;
  owner: string;
  repo: string;
  branch: string;
} {
  const sourceLocation = input as string;
  const parts =
    sourceLocation?.match(
      /(?<protocol>https?):\/\/(?<host>[^/]+)\/(?<owner>[^/]+)\/(?<repo>[^/]+)\/(blob|tree)\/(?<branch>[^/]+)\/?/,
    )?.groups ?? {};
  return {
    branch: parts.branch ?? '',
    host: parts.host ?? '',
    owner: parts.owner ?? '',
    protocol: parts.protocol ?? '',
    repo: parts.repo ?? '',
  };
}

/**
 *
 * Parses ownershipRefs and return matched groups
 * @param input A string in the form of `group:github-org/devhub-group`
 *
 */
export function parseOwnershipRef(input: string): {
  orgName: string;
  group: string;
} {
  const sourceLocation = input as string;
  const parts =
    sourceLocation?.match(/^group:(?<group>(\S+))\/(?<org>(\S+))/)?.groups ??
    {};
  return {
    orgName: parts.group ?? '',
    group: parts.org ?? '',
  };
}

/**
 * serializeRepoPickerUrl takes a RepoUrlPickerState object and returns a serialized URL
 * @param data - The RepoUrlPickerState object used to generate the URL
 * @returns  The serialized URL or undefined if the host is not provided
 */
export function serializeRepoPickerUrl(data: RepoUrlPickerState) {
  if (!data.host) {
    return undefined;
  }

  const params = new URLSearchParams();
  if (data.owner) {
    params.set('owner', data.owner);
  }
  if (data.repoName) {
    params.set('repo', data.repoName);
  }
  if (data.organization) {
    params.set('organization', data.organization);
  }
  if (data.workspace) {
    params.set('workspace', data.workspace);
  }
  if (data.project) {
    params.set('project', data.project);
  }

  return `${data.host}?${params.toString()}`;
}

/**
 * Parses a URL string for a repository URL picker.
 * @param  url - The URL string to parse.
 * @returns An object containing the parsed parameters from the URL.
 */
export function parseRepoPickerUrl(
  url: string | undefined,
): RepoUrlPickerState {
  let host = '';
  let owner = '';
  let repoName = '';
  let organization = '';
  let workspace = '';
  let project = '';

  try {
    if (url) {
      const parsed = new URL(`https://${url}`);
      host = parsed.host;
      owner = parsed.searchParams.get('owner') || '';
      repoName = parsed.searchParams.get('repo') || '';
      organization = parsed.searchParams.get('organization') || '';
      workspace = parsed.searchParams.get('workspace') || '';
      project = parsed.searchParams.get('project') || '';
    }
  } catch {
    /* ok */
  }

  return { host, owner, repoName, organization, workspace, project };
}
